import { Facebook, Instagram, Youtube, Music2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
  spotify: Music2,
};

export const Footer = () => {
  const s = useSiteSettings();
  const socials = [
    { name: "facebook", href: s.facebook_url || "#" },
    { name: "instagram", href: s.instagram_url || "#" },
    { name: "youtube", href: s.youtube_url || "#" },
    { name: "spotify", href: s.spotify_artist_url || "#" },
  ].filter((x) => x.href && x.href !== "#");
  return (
    <footer
      data-testid="footer"
      className="relative bg-black border-t border-white/5 pt-20 pb-10 overflow-hidden grain-overlay"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-16">
        {/* Giant brand */}
        <div className="text-center mb-12">
          <h2
            className="font-anton uppercase text-[#B91C1C] leading-[0.85] text-[22vw] md:text-[18vw] lg:text-[14vw] tracking-tight select-none"
            style={{ textShadow: "0 0 60px rgba(185,28,28,0.25)" }}
          >
            Loveless
          </h2>
          <p className="font-oswald uppercase tracking-[0.4em] text-white/40 text-xs md:text-sm -mt-2 md:-mt-4">
            Classic Rock · Montana · Forever
          </p>
        </div>

        <div className="border-t border-white/10 pt-10 grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          <div className="text-white/50 font-manrope text-sm">
            © 2026 The Randy Loveless Band.
            <br />
            All the classics, all night long.
          </div>

          <div className="flex justify-center gap-3">
            {socials.length === 0 ? (
              <span className="font-oswald uppercase tracking-[0.2em] text-[10px] text-white/30">
                Add social links in admin settings
              </span>
            ) : (
              socials.map((sc) => {
                const Icon = ICONS[sc.name];
                return (
                  <a
                    key={sc.name}
                    href={sc.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-social-link-${sc.name}`}
                    aria-label={sc.name}
                    className="w-11 h-11 border border-white/15 hover:border-[#B91C1C] hover:bg-[#B91C1C] text-white/70 hover:text-white flex items-center justify-center transition-colors"
                  >
                    <Icon size={18} />
                  </a>
                );
              })
            )}
          </div>

          <div className="md:text-right text-white/50 font-manrope text-sm">
            <a
              href={`mailto:${s.booking_email || "booking@randylovelessband.com"}`}
              data-testid="footer-booking-link"
              className="hover:text-[#DC2626] transition-colors"
            >
              {s.booking_email || "booking@randylovelessband.com"}
            </a>
            <div className="mt-1 text-white/30 text-xs font-oswald uppercase tracking-[0.2em]">
              Built by{" "}
              <a
                href="https://jbwebstudios.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-jbweb-link"
                className="hover:text-[#DC2626] transition-colors"
              >
                JB Web Studios
              </a>
            </div>
          </div>
        </div>

        {/* Legal + discreet admin */}
        <div className="mt-8 pt-6 border-t border-white/5 flex flex-wrap items-center justify-between gap-3 text-[10px] font-oswald uppercase tracking-[0.25em] text-white/30">
          <div className="flex gap-5">
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="hover:text-white/70 transition-colors"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              data-testid="footer-terms-link"
              className="hover:text-white/70 transition-colors"
            >
              Terms
            </Link>
          </div>
          <Link
            to="/admin/login"
            data-testid="footer-admin-link"
            aria-label="Admin"
            className="text-white/15 hover:text-white/60 transition-colors font-oswald uppercase tracking-[0.3em] text-[10px]"
          >
            Admin
          </Link>
        </div>
      </div>
    </footer>
  );
};
